import { useState, FormEvent } from 'react';

export default function BookingForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    serviceType: '',
    preferredDate: '',
    preferredTime: '',
    itemDescription: ''
  });

  const serviceTypes = [
    'Furniture & Appliances',
    'Garage & Storage Cleanout',
    'Eviction & Foreclosure Cleanup',
    'Bulky Items Removal',
    'Curbside Pickup',
    'Donation Pickup',
    'Estate Cleanout',
    'Yard Waste Removal',
    'Construction Debris',
    'Other'
  ];

  const timeSlots = [
    'Morning (8AM - 12PM)',
    'Afternoon (12PM - 4PM)',
    'Evening (4PM - 7PM)'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const message = `HAUL IT ALL - New Pickup Booking\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nAddress: ${formData.address}\nCity: ${formData.city}\nService: ${formData.serviceType}\nDate: ${formData.preferredDate}\nTime: ${formData.preferredTime}\n\nItems to Haul:\n${formData.itemDescription}`;

      const smsUrl = `sms:9513509072?body=${encodeURIComponent(message)}`;
      window.open(smsUrl, '_self');

      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        serviceType: '',
        preferredDate: '',
        preferredTime: '',
        itemDescription: ''
      });
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (isSubmitted) {
    return (
      <section id="booking" className="py-24 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)',
            backgroundSize: '80px 80px'
          }}></div>
        </div>
        
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-gray-900/80 border border-cyan-500/30 rounded-2xl p-12 text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-4xl text-white"></i>
            </div>
            <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              BOOKING RECEIVED!
            </h3>
            <p className="text-gray-400 text-lg mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              Thank you for choosing HAUL IT ALL. Carlos will contact you within 24 hours to confirm your pickup appointment.
            </p>
            <button
              onClick={() => setIsSubmitted(false)}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-3 rounded-lg font-bold hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              Schedule Another Pickup
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="py-24 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }}></div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-cyan-400 text-sm font-bold tracking-[0.3em] uppercase mb-4" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            <span className="w-8 h-px bg-cyan-400"></span>
            Easy Scheduling
            <span className="w-8 h-px bg-cyan-400"></span>
          </span>
          <h2 
            className="text-5xl md:text-6xl font-black text-white mb-4"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            BOOK A PICKUP
          </h2>
          <p className="text-xl text-gray-400" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            Schedule your junk removal in minutes
          </p>
        </div>

        <form 
          id="pickup-booking-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-gray-900/80 border border-gray-800 rounded-2xl p-8 md:p-10"
        >
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Name */}
            <div>
              <label className="block text-gray-300 text-sm font-semibold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors text-sm"
                placeholder="Your full name"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-gray-300 text-sm font-semibold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors text-sm"
                placeholder="your@email.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-gray-300 text-sm font-semibold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors text-sm"
                placeholder="(951) 000-0000"
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-gray-300 text-sm font-semibold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                City *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors text-sm"
                placeholder="Riverside, Corona, etc."
              />
            </div>
          </div>

          {/* Address */}
          <div className="mb-6">
            <label className="block text-gray-300 text-sm font-semibold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              Pickup Address *
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors text-sm"
              placeholder="Street address"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {/* Service Type */}
            <div>
              <label className="block text-gray-300 text-sm font-semibold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Service Type *
              </label>
              <select
                name="serviceType"
                value={formData.serviceType}
                onChange={handleChange}
                required
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors text-sm cursor-pointer"
              >
                <option value="">Select service</option>
                {serviceTypes.map((type, index) => (
                  <option key={index} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Preferred Date */}
            <div>
              <label className="block text-gray-300 text-sm font-semibold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Preferred Date *
              </label>
              <input
                type="date"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
                min={getTomorrowDate()}
                required
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors text-sm cursor-pointer"
              />
            </div>

            {/* Preferred Time */}
            <div>
              <label className="block text-gray-300 text-sm font-semibold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Preferred Time *
              </label>
              <select
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
                required
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors text-sm cursor-pointer"
              >
                <option value="">Select time</option>
                {timeSlots.map((slot, index) => (
                  <option key={index} value={slot}>{slot}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Item Description */}
          <div className="mb-8">
            <label className="block text-gray-300 text-sm font-semibold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              What do you need hauled? *
            </label>
            <textarea
              name="itemDescription"
              value={formData.itemDescription}
              onChange={handleChange}
              required
              maxLength={500}
              rows={4}
              className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors text-sm resize-none"
              placeholder="Describe the items you need removed (e.g., old couch, broken refrigerator, garage full of boxes...)"
            />
            <p className="text-gray-500 text-xs mt-1">{formData.itemDescription.length}/500 characters</p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-4 rounded-lg font-bold text-lg hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin text-xl"></i>
                Submitting...
              </>
            ) : (
              <>
                <i className="ri-calendar-check-line text-xl"></i>
                Schedule My Pickup
              </>
            )}
          </button>

          <p className="text-center text-gray-500 text-sm mt-4" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            <i className="ri-phone-line mr-1"></i>
            Need immediate service? Call Carlos directly at <a href="tel:9513509072" className="text-cyan-400 hover:underline cursor-pointer">(951) 350-9072</a>
          </p>
        </form>
      </div>
    </section>
  );
}
